import { useEffect, useState } from "react";
import { adminLogin, fetchLeads } from "../lib/api";
import { toast } from "sonner";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_57d85e2a-e3d9-489b-aea5-c3202d03fe13/artifacts/xzltz4c8_ChatGPT%20Image%208%20de%20mai.%20de%202026%2C%2018_11_09.png";

const TOKEN_KEY = "fluxo_admin_token";

function LoginForm({ onAuth }) {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!password) return;
        setLoading(true);
        try {
            const { data } = await adminLogin(password);
            localStorage.setItem(TOKEN_KEY, data.token);
            onAuth(data.token);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Credenciais inválidas.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-md border border-white/10 bg-[#0d0d0d] p-10"
            >
                <div className="flex items-center gap-3 mb-10">
                    <img src={LOGO_URL} alt="" className="h-10 w-10" />
                    <div className="leading-none">
                        <div className="text-[10px] tag-fluxo">FLUXO</div>
                        <div className="text-[11px] text-white/80 tracking-[0.32em] font-medium font-['Outfit']">
                            ACESSO RESTRITO
                        </div>
                    </div>
                </div>
                <h1 className="font-['Outfit'] font-bold uppercase text-2xl mb-2 tracking-tight">
                    Painel Interno
                </h1>
                <p className="text-white/40 text-sm mb-8">
                    Acesso exclusivo ao comitê operacional.
                </p>
                <div className="label-fluxo mb-2">Senha</div>
                <input
                    data-testid="admin-password"
                    type="password"
                    className="fluxo-input mb-8"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                />
                <button
                    data-testid="admin-login-submit"
                    disabled={loading}
                    className="btn-gold w-full py-3 text-[12px] uppercase tracking-[0.28em] font-bold font-['Outfit'] disabled:opacity-60"
                >
                    <span>{loading ? "Verificando…" : "Entrar"}</span>
                </button>
            </form>
        </div>
    );
}

function Dashboard({ token, onLogout }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        try {
            const { data } = await fetchLeads(token);
            setLeads(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                onLogout();
                return;
            }
            toast.error("Erro ao carregar leads.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-dashboard">
            <header className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="" className="h-9 w-9" />
                        <div className="leading-none">
                            <div className="text-[10px] tag-fluxo">FLUXO</div>
                            <div className="text-[11px] text-white/80 tracking-[0.32em] font-medium font-['Outfit']">
                                PAINEL INTERNO
                            </div>
                        </div>
                    </a>
                    <button
                        data-testid="admin-logout"
                        onClick={onLogout}
                        className="text-[11px] uppercase tracking-[0.28em] text-white/60 hover:text-[#d4af37] transition-colors font-['Outfit']"
                    >
                        Sair
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="tag-fluxo mb-3">Candidaturas Recebidas</div>
                        <h1 className="font-['Outfit'] font-bold uppercase text-3xl md:text-4xl tracking-tight">
                            Leads em Análise
                        </h1>
                    </div>
                    <div className="border border-[#d4af37]/30 px-6 py-4" data-testid="leads-total">
                        <div className="text-[10px] tag-fluxo mb-1">Total</div>
                        <div className="text-3xl font-['Outfit'] font-bold text-gold-gradient">
                            {leads.length}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-white/40 text-sm">Carregando…</div>
                ) : leads.length === 0 ? (
                    <div className="border border-white/5 bg-[#0d0d0d] p-16 text-center">
                        <p className="text-white/40">Nenhum lead recebido ainda.</p>
                    </div>
                ) : (
                    <div className="border border-white/5 bg-[#0d0d0d] overflow-x-auto" data-testid="leads-table">
                        <table className="w-full text-sm">
                            <thead className="bg-black/40">
                                <tr className="text-left">
                                    {["Data", "Nome", "Empresa", "E-mail", "Faturamento", "Segmento", ""].map(
                                        (h) => (
                                            <th
                                                key={h}
                                                className="px-5 py-4 text-[10px] tag-fluxo font-medium"
                                            >
                                                {h}
                                            </th>
                                        )
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((l, i) => (
                                    <tr
                                        key={l.id}
                                        data-testid={`lead-row-${i}`}
                                        className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                                    >
                                        <td className="px-5 py-4 text-white/50 whitespace-nowrap">
                                            {new Date(l.created_at).toLocaleString("pt-BR")}
                                        </td>
                                        <td className="px-5 py-4 text-white">{l.nome}</td>
                                        <td className="px-5 py-4 text-white/70">{l.empresa}</td>
                                        <td className="px-5 py-4 text-white/70">{l.email}</td>
                                        <td className="px-5 py-4 text-[#d4af37]">{l.faturamento}</td>
                                        <td className="px-5 py-4 text-white/70">{l.segmento}</td>
                                        <td className="px-5 py-4 text-right">
                                            <button
                                                onClick={() => setSelected(l)}
                                                className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37] hover:text-[#f5d76e] font-['Outfit']"
                                            >
                                                Detalhes
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {selected && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
                    onClick={() => setSelected(null)}
                    data-testid="lead-detail-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-2xl border border-[#d4af37]/30 bg-[#0a0a0a] p-10"
                    >
                        <div className="flex items-start justify-between mb-8">
                            <div>
                                <div className="tag-fluxo mb-2">Candidatura</div>
                                <h2 className="font-['Outfit'] font-bold uppercase text-2xl tracking-tight">
                                    {selected.nome}
                                </h2>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="text-white/40 hover:text-[#d4af37] text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                            {[
                                ["Empresa", selected.empresa],
                                ["E-mail", selected.email],
                                ["Telefone", selected.telefone],
                                ["Faturamento", selected.faturamento],
                                ["Segmento", selected.segmento],
                                [
                                    "Data",
                                    new Date(selected.created_at).toLocaleString("pt-BR"),
                                ],
                            ].map(([k, v]) => (
                                <div key={k}>
                                    <dt className="label-fluxo mb-1">{k}</dt>
                                    <dd className="text-white">{v}</dd>
                                </div>
                            ))}
                        </dl>
                        {selected.mensagem && (
                            <div className="mt-8 pt-6 border-t border-white/5">
                                <div className="label-fluxo mb-2">Mensagem</div>
                                <p className="text-white/70 leading-relaxed">
                                    {selected.mensagem}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
    };

    if (!token) return <LoginForm onAuth={setToken} />;
    return <Dashboard token={token} onLogout={logout} />;
}
